import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useSearch from '../../hooks/useSearch';
import { SearchResult } from '../../store/search';
import ResultCard from './ResultCard';
import ResultColumnHeader from './ResultColumnHeader';
import { useRoutes } from '../../hooks/useRoutes';
import useContractorManagementDialog from '../../store/contractorManagementDialog';
import useFiltering from '../../hooks/useFiltering';
import { useEntitySchema } from '../../hooks/useEntitySchema';
import { useFilterSets } from '../../hooks/useFilterSets';
import Input from '../core/Input';
import { IconSearch } from '@/icons';
import { filterSetToExecutionFilters } from '../../helpers/filter_helpers';

interface SearchResultsContainerProps {
  /** Overrides default behavior of showing the results container when store has results. */
  show?: boolean;
  /** Clear the search results when a result is clicked. Defaults to true. */
  clearOnClick?: boolean;
}

const resultColumnOrder: SearchResult['type'][] = [
  'entityGroup',
  'workorder',
  'contractor',
  'filterSet',
];

const SearchResultContainer: FC<SearchResultsContainerProps> = ({
  show,
  clearOnClick,
}) => {
  const { results, doSearch, searchTerm, clearSearch } = useSearch();
  const {
    setShow: setContractorManagementDialogOpen,
    setSelectedContractorId,
  } = useContractorManagementDialog();
  const [input, setInput] = useState(searchTerm || '');
  const { filter } = useFiltering();
  const { data: schema } = useEntitySchema();
  const { lookup: filterSetLookup } = useFilterSets();
  const route = useRoutes();

  const onChange = useCallback(
    (value: string) => {
      setInput(value);
      doSearch(value);
    },
    [doSearch],
  );
  const onClearSearch = useCallback(() => {
    clearSearch();
    setInput('');
  }, [clearSearch]);

  const status = useMemo<'zero_results' | 'no_search' | 'success'>(() => {
    if (!results || input.trim().length === 0) {
      return 'no_search';
    } else if (
      results.contractor.length === 0 &&
      results.filterSet.length === 0 &&
      results.entityGroup.length === 0 &&
      results.workorder.length === 0
    ) {
      return 'zero_results';
    }
    return 'success';
  }, [input, results]);

  const onResultClick = useCallback(
    (result: SearchResult, action?: string) => {
      const type = result.type;
      if (clearOnClick !== false) {
        onClearSearch();
      }
      if (type === 'entityGroup') {
        route.goToStationDetails(result.id);
      } else if (type === 'workorder') {
        route.goToWorkOrderDetails(result.id);
      } else if (type === 'contractor') {
        if (action === 'manage') {
          onClearSearch();
          setSelectedContractorId(result.id);
          setContractorManagementDialogOpen(true);
        } else {
          route.goToWorkOrderOverview();
        }
      } else if (type === 'filterSet') {
        route.goToRoot();
        const filterSet = filterSetLookup[result.id];
        if (filterSet && schema) {
          filter(schema, filterSetToExecutionFilters(filterSet), false);
        }
      } else {
        console.warn(
          `Click on '${result.type}' search result not implemented.`,
        );
      }
    },
    [
      clearOnClick,
      onClearSearch,
      route,
      setSelectedContractorId,
      setContractorManagementDialogOpen,
      filterSetLookup,
      schema,
      filter,
    ],
  );

  useEffect(() => {
    if (results && searchTerm) {
      setInput(searchTerm || '');
    }
  }, [results, searchTerm]);

  const onKeyDown = useCallback(
    (key: string) => {
      if (key === 'Escape') {
        onClearSearch();
      }
    },
    [onClearSearch],
  );

  if (show === false || searchTerm === null) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100000] min-h-screen w-full overflow-y-hidden overflow-x-scroll bg-[rgba(230,230,230,0.9)] px-[3%]"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClearSearch();
      }}
    >
      <div className="flex flex-col items-center">
        <div className="flex flex-col">
          <Input
            id="search-field"
            startAdornment={<IconSearch />}
            value={input}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => onKeyDown(e.key)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <p className="text-center">
            <FormattedMessage
              id="search.how_to_close_info"
              defaultMessage="Press Escape or click anywhere on the background to cancel searching."
            />
          </p>
        </div>
        {status === 'zero_results' && (
          <span className="mt-5 font-sans text-xl font-medium text-text-600">
            <FormattedMessage
              id="search.zero_results"
              defaultMessage="The search has returned no results"
            />
          </span>
        )}
        {status === 'no_search' && (
          <span className="mt-5 font-sans text-xl font-medium text-text-600">
            <FormattedMessage
              id="search.zero_results"
              defaultMessage="Use the search box above"
            />
          </span>
        )}
      </div>
      {status === 'success' && (
        <div className="flex flex-row content-between">
          {resultColumnOrder.map((colType) => {
            const res = results?.[colType] || [];
            return (
              <div className="m-3 flex w-[22vw] flex-col" key={colType}>
                <div className="w-[22vw]">
                  <ResultColumnHeader type={colType} empty={res.length === 0} />
                </div>
                {res.slice(0, 6).map((result, i) => (
                  <div key={result.type + '-' + result.id} className="mb-3">
                    <ResultCard
                      result={result}
                      onClick={onResultClick}
                      noTopRadius={i === 0}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchResultContainer;
